/**
 * Field clusters (plan R7, D-TBD 6): the dense parts of a field are M1 fields. Each cluster is M1's `generateField`
 * with its own seed, translated so the M1 envelope centre lands on the cluster centre, so every rock in it is exactly
 * an M1 rock. The home cluster (`authored: 'm1-home'`) is the sandbox field itself: M1's seed and spawn pocket,
 * translated so the M1 frame origin lands on the field centre (the home cluster centre, D-TBD 7).
 */
import { createRng, nextFloat, type Rng } from '../core/rng'
import type { FieldConfig } from '../data/schemas'
import { deriveSeed } from '../galaxy/seed'
import type { FieldSite, PoiSite, Vec3T } from '../galaxy/layout/types'
import type { QualityPreset } from '../render/contracts'
import { generateField } from '../world/field'
import type { StreamingConfig } from './config'
import { rockKey } from './keys'
import { translateLayout, type RockPart } from './merge'

/** One cluster of a field. For the home cluster, `pocket` is M1's spawn pocket in the M1 frame; otherwise null. */
export interface ClusterSite {
  readonly index: number
  /** Where the M1 envelope centre lands (float64 system frame); for the home cluster, where the M1 origin lands. */
  readonly center: Vec3T
  readonly seed: number
  readonly authored: boolean
  readonly pocket: Vec3T | null
}

/** What the home cluster needs from M1: `world.json`'s seed and spawn position. */
export interface M1Home {
  readonly seed: number
  readonly spawn: Vec3T
}

/** A spawn pocket far outside any cluster: generated clusters carve no pocket (M1's lanes and derelict clearance stay). */
const FAR_POCKET = { x: 1e15, y: 1e15, z: 1e15 } as const

/** A uniform point in the ball of `radius`, written into `out`. */
function pointInBall(rng: Rng, radius: number, out: number[]): void {
  let x: number
  let y: number
  let z: number
  do {
    x = nextFloat(rng) * 2 - 1
    y = nextFloat(rng) * 2 - 1
    z = nextFloat(rng) * 2 - 1
  } while (x * x + y * y + z * z > 1)
  out[0] = x * radius
  out[1] = y * radius
  out[2] = z * radius
}

/**
 * The field's clusters: a seeded count from `clusters.count[kind]`, centres uniform within `placementFraction` of the
 * field radius and at least `minSeparationM` apart when `maxTries` allows, and a seed per site. An authored home field
 * has exactly one cluster, the M1 field (which needs `home`).
 */
export function clusterSites(field: FieldSite, cfg: StreamingConfig, home?: M1Home): ClusterSite[] {
  if (field.authored === 'm1-home') {
    if (!home)
      throw new Error(`clusterSites: field ${field.id} is the authored home field and needs the M1 seed and spawn`)
    return [
      {
        index: 0,
        center: field.center,
        seed: home.seed,
        authored: true,
        pocket: home.spawn,
      },
    ]
  }
  const rng = createRng(deriveSeed(field.seed, 'clusters'))
  const [min, max] = cfg.clusters.count[field.kind]
  const n = min + Math.floor(nextFloat(rng) * (max - min + 1))
  const placeR = cfg.clusters.placementFraction * field.radiusM
  const minSep = cfg.clusters.minSeparationM
  const sites: ClusterSite[] = []
  const p = [0, 0, 0]
  for (let i = 0; i < n; i++) {
    let best: Vec3T = field.center
    let bestGap = -1
    for (let t = 0; t < cfg.clusters.maxTries; t++) {
      pointInBall(rng, placeR, p)
      const c: Vec3T = [field.center[0] + p[0], field.center[1] + p[1], field.center[2] + p[2]]
      let gap = Infinity
      for (const s of sites) gap = Math.min(gap, Math.hypot(c[0] - s.center[0], c[1] - s.center[1], c[2] - s.center[2]))
      if (gap > bestGap) {
        best = c
        bestGap = gap
      }
      if (gap >= minSep) break
    }
    sites.push({
      index: i,
      center: best,
      seed: deriveSeed(field.seed, 'cluster', i),
      authored: false,
      pocket: null,
    })
  }
  return sites
}

/** The system-frame translation from the M1 frame for a site. */
function offsetOf(site: ClusterSite, fieldCfg: FieldConfig): Vec3T {
  if (site.authored) return site.center
  const e = fieldCfg.envelope.center
  return [site.center[0] - e[0], site.center[1] - e[1], site.center[2] - e[2]]
}

/**
 * An anomaly hotspot (M3 R22i, plan §7 item 6): one M1 cluster with the anomaly's seed, placed so M1's derelict
 * clearance (inside its metal-rich derelict clump) lands on the anomaly. Its field is the anomaly itself (keys
 * `rockKey(poi.id, 'c0', …)`); it is not one of the layout's fields, so it has no background cells.
 */
export function hotspotCluster(poi: Pick<PoiSite, 'id' | 'position' | 'seed'>, fieldCfg: FieldConfig): { field: FieldSite; site: ClusterSite } {
  const d = fieldCfg.derelict.position
  const e = fieldCfg.envelope.center
  const p = poi.position
  const field: FieldSite = { id: poi.id, index: -1, kind: 'asteroid', center: p, radiusM: 0, oreWeights: {}, seed: poi.seed, authored: null }
  const site: ClusterSite = { index: 0, center: [p[0] - d[0] + e[0], p[1] - d[1] + e[1], p[2] - d[2] + e[2]], seed: poi.seed, authored: false, pocket: null }
  return { field, site }
}

/** The cluster's bounding sphere (the translated M1 envelope), for its streaming candidate. */
export function clusterBounds(site: ClusterSite, fieldCfg: FieldConfig): { center: Vec3T; radiusM: number } {
  const o = offsetOf(site, fieldCfg)
  const e = fieldCfg.envelope
  return {
    center: [e.center[0] + o[0], e.center[1] + o[1], e.center[2] + o[2]],
    radiusM: Math.max(e.semiAxes[0], e.semiAxes[1], e.semiAxes[2]),
  }
}

/**
 * Generates the cluster: M1's `generateField(fieldCfg, site.seed, preset, pocket)` translated into the system frame.
 * Keys are `rockKey(field.id, 'c<index>', …)` over the rock's M1-frame position.
 */
export function generateCluster(
  field: FieldSite,
  site: ClusterSite,
  fieldCfg: FieldConfig,
  preset: QualityPreset,
): RockPart {
  const pocket = site.pocket ? { x: site.pocket[0], y: site.pocket[1], z: site.pocket[2] } : FAR_POCKET
  const m1 = generateField(fieldCfg, site.seed, preset, pocket)
  const part = `c${site.index}`
  const keys: string[] = new Array<string>(m1.count)
  for (let i = 0; i < m1.count; i++) keys[i] = rockKey(field.id, part, m1.x[i], m1.y[i], m1.z[i])
  const o = offsetOf(site, fieldCfg)
  return { layout: translateLayout(m1, o[0], o[1], o[2]), keys }
}
